// RawUploadPanel — instructional dialog for the file_upload channel. Shows
// the per-source /upload URL with a copy button, a drag-and-drop demo
// area that uploads in-place, and the accepted MIME / format list. This
// is the "give a partner a URL" surface, distinct from UploadDialog
// which is the actual file-picker for one-off operator uploads.
//
// BACKLOG: Inputs Rail · Public upload form panel.

import React, { useState } from 'react';
import type { Source } from '@datahub/shared';
import { useToast } from '../Toast';

const ACCEPTED_FORMATS: Array<{ ext: string; mime: string; kind: string }> = [
  { ext: '.json', mime: 'application/json', kind: 'JSON object or array' },
  { ext: '.ndjson', mime: 'application/x-ndjson', kind: 'newline-delimited JSON' },
  { ext: '.csv', mime: 'text/csv', kind: 'header row + data' },
  { ext: '.xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', kind: 'first sheet, header row' },
  { ext: '.xls', mime: 'application/vnd.ms-excel', kind: 'first sheet, header row' },
  { ext: '.zip', mime: 'application/zip', kind: 'recursively expanded' },
  { ext: '.gz', mime: 'application/gzip', kind: 'auto-decompressed' },
  { ext: '.tar', mime: 'application/x-tar', kind: 'recursively expanded' },
  { ext: '.tar.gz', mime: 'application/x-tar', kind: 'recursively expanded' },
  { ext: '.xml', mime: 'application/xml', kind: 'parsed tree as event' },
  { ext: '.pdf', mime: 'application/pdf', kind: 'stored as attachment (no parsing)' },
];

export function RawUploadPanel({ source, onClose }: { source: Source; onClose: () => void }): React.ReactElement {
  const toast = useToast();
  const fileInput = source.inputs.find(i => i.kind === 'file_upload');
  const url = fileInput?.url ?? `${window.location.origin}/u/${source.slug_alias ?? source.source_id}`;
  const status = fileInput?.status ?? 'unknown';
  const [copied, setCopied] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function copyUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.show('URL copied.', { kind: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.show('Clipboard write failed — copy manually.', { kind: 'error' });
    }
  }

  async function handleFile(f: File): Promise<void> {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'X-Filename': f.name },
        body: f,
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        const accepted = (body as { accepted?: number }).accepted ?? 0;
        toast.show(`Uploaded "${f.name}" — ${accepted} event${accepted === 1 ? '' : 's'}.`, { kind: 'success' });
      } else {
        const err = body as { error?: { code?: string; detail?: string } };
        toast.show(`Upload failed · ${err.error?.detail ?? err.error?.code ?? `HTTP ${res.status}`}`, { kind: 'error' });
      }
    } catch (e) {
      toast.show(`Upload failed · ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setUploading(false);
    }
  }

  React.useEffect(() => {
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'oklch(0.18 0.01 250 / 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 60 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Public upload" style={{ width: 'min(640px, calc(100vw - 48px))', background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12, padding: 24, maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Public upload</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 6px', borderRadius: 3, color: status === 'active' ? 'oklch(0.42 0.12 150)' : 'var(--ink-3)', background: status === 'active' ? 'oklch(0.92 0.06 150)' : 'var(--bg)', border: `1px solid ${status === 'active' ? 'oklch(0.78 0.10 150)' : 'var(--line)'}` }}>{status}</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 16, lineHeight: 1.5 }}>
          Hand a partner this URL. They <code>POST</code> a file and each row (or the file itself) becomes an event on this Source. Same pipeline as the operator upload dialog — just remote-friendly.
        </div>

        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 6 }}>Upload URL</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6, marginBottom: 16 }}>
          <code style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</code>
          <button type="button" className="btn sm" onClick={() => { void copyUrl(); }} title="Copy URL to clipboard">{copied ? 'copied ✓' : 'copy'}</button>
        </div>

        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 6 }}>Try it (drag a file)</div>
        <label
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void handleFile(f);
          }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '24px 16px', marginBottom: 16,
            border: `1px dashed ${dragOver ? 'var(--accent, oklch(0.55 0.18 250))' : 'var(--line)'}`,
            borderRadius: 8, background: dragOver ? 'oklch(0.95 0.04 250)' : 'var(--bg-sunken)',
            color: 'var(--ink-2)', fontSize: 12.5, textAlign: 'center', cursor: 'pointer',
            transition: 'border-color 120ms, background 120ms',
          }}
        >
          {uploading ? 'Uploading…' : (
            <>
              <span style={{ fontSize: 13, marginBottom: 4 }}>Drop a file here, or click to pick</span>
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>POSTs to the URL above. Toast shows the accepted event count.</span>
            </>
          )}
          <input
            type="file"
            style={{ display: 'none' }}
            disabled={uploading}
            onChange={e => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ''; }}
          />
        </label>

        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 6 }}>Accepted formats</div>
        <div style={{ marginBottom: 16, border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
            <tbody>
              {ACCEPTED_FORMATS.map((f, i) => (
                <tr key={f.ext} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--line-2)' }}>
                  <td style={{ padding: '6px 10px', width: 80, fontFamily: 'var(--mono)', color: 'var(--ink)', fontWeight: 500 }}>{f.ext}</td>
                  <td style={{ padding: '6px 10px', color: 'var(--ink-2)' }}>{f.kind}</td>
                  <td style={{ padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.mime}>{f.mime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 16 }}>
          Cap: 100 MB per upload. <code>X-Filename</code> header preserves the original filename in the event lineage. <code>Idempotency-Key</code> header lets a partner safely retry on socket timeout.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
